import React, { createContext, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  PropTypes,
  Typography,
} from '@material-ui/core'

export type AlertButton = {
  dataCy?: string
  title: string
  variant?: 'text' | 'outlined' | 'contained'
  color?: PropTypes.Color
  onPress?: () => Promise<void>
}

export type AlertContextValue = {
  isOpen: boolean
  title: string
  message: string
  alert: (title: string, message: string, buttons?: AlertButton[]) => void
}

export const AlertContext = createContext<AlertContextValue>({
  isOpen: false,
  title: '',
  message: '',
  alert: () => {},
})

type Props = {
  children: React.ReactNode
}

export function AlertProvider({ children }: Props): React.ReactElement {
  const [isOpen, setIsOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [message, setMessage] = useState<string>('')
  const [buttons, setButtons] = useState<AlertButton[]>([])
  const { t } = useTranslation()

  const alertContext = useMemo((): AlertContextValue => {
    const defaultButtons = [{ title: t('common.ok') }]

    return {
      isOpen,
      title,
      message,
      alert: (
        alertTitle: string,
        alertMessage: string,
        alertButtons?: AlertButton[],
      ) => {
        setTitle(alertTitle)
        setMessage(alertMessage)
        setButtons(alertButtons ?? defaultButtons)
        setIsOpen(true)
      },
    }
  }, [isOpen, title, message, t])

  return (
    <AlertContext.Provider value={alertContext}>
      <Dialog open={isOpen}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          <Typography>{message}</Typography>
        </DialogContent>
        <DialogActions>
          {buttons.map((button, index) => (
            <Button
              key={button.title}
              id={`alert-button-${index}`}
              data-cy={button.dataCy}
              variant={button.variant ?? 'contained'}
              color={button.color}
              onClick={async () => {
                setIsOpen(false)

                if (button.onPress) {
                  await button.onPress()
                }
              }}
            >
              {button.title}
            </Button>
          ))}
        </DialogActions>
      </Dialog>
      {children}
    </AlertContext.Provider>
  )
}

export const useAlert = (): AlertContextValue => useContext(AlertContext)
